import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MdSearch, MdFolderOpen, MdDescription, MdMoreVert, MdLocationOn, MdBrandingWatermark, MdCancel, MdInfo, MdKeyboardArrowDown, MdSave, MdEdit, MdDelete, MdVisibility, MdAdd, MdDownload } from 'react-icons/md';
import { ClearFiltersButton, PageHeader, PageSkeleton } from '@euroasiann/shared/ui';
import { authenticatedFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import { formatServerDate, formatServerTime, canExportRfqData } from '@euroasiann/shared';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/shared/Toast';
import { ExportDataModal } from '../../components/shared/ExportDataModal';
import {
  downloadRfqDataExcel,
  downloadRfqDataPdf,
  type RfqExportFormat,
  type RfqExportRow,
} from '../../utils/rfq-data-export';

const LOCAL_DRAFT_KEY = 'admin_rfq_draft';

interface RFQ {
  _id: string;
  rfqNumber: string;
  title: string;
  senderType?: 'customer' | 'admin';
  vesselId?: {
    _id: string;
    name: string;
    imoNumber?: string;
  };
  vesselName?: string;
  supplyPort?: string;
  brand?: string;
  category?: string;
  subCategory?: string;
  equipmentModel?: string;
  department?: string;
  equipmentTags?: string;
  status: string;
  createdAt: string;
  dueDate?: string;
  organizationId?: {
    _id: string;
    name: string;
  };
  metadata?: Record<string, unknown>;
  isLocalDraft?: boolean;
}

type PipelineTab = 'all' | 'drafts' | 'unquoted' | 'quoted' | 'unconfirmed_po' | 'confirmed_po' | 'rejected';

const PIPELINE_TABS: PipelineTab[] = [
  'all',
  'drafts',
  'unquoted',
  'quoted',
  'unconfirmed_po',
  'confirmed_po',
  'rejected',
];

function parsePipelineTab(value: string | null): PipelineTab {
  if (value && PIPELINE_TABS.includes(value as PipelineTab)) {
    return value as PipelineTab;
  }
  return 'all';
}

function readLocalDraftRfq(): RFQ | null {
  try {
    const raw = localStorage.getItem(LOCAL_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const form = parsed?.formData || {};
    const items = Array.isArray(parsed?.items) ? parsed.items : [];
    const meaningful =
      form.vesselName ||
      form.category ||
      form.brand ||
      form.model ||
      form.equipmentTags ||
      form.remarks ||
      form.imoNo ||
      (form.selectedVendorIds && form.selectedVendorIds.length > 0) ||
      items.some(
        (i: {
          itemDescription?: string;
          partNo?: string;
          impaNo?: string;
          requiredQuantity?: string;
        }) => i.itemDescription || i.partNo || i.impaNo || i.requiredQuantity
      );
    if (!meaningful) return null;
    return {
      _id: 'local-draft',
      rfqNumber: 'LOCAL-DRAFT',
      title: form.vesselName
        ? `Draft RFQ for ${form.vesselName}${form.category ? ` - ${form.category}` : ''}`
        : 'Unnamed RFQ draft',
      vesselName: form.vesselName || '',
      vesselId: form.vesselName
        ? { _id: 'local', name: form.vesselName, imoNumber: form.imoNo }
        : undefined,
      brand: form.brand || '',
      category: form.category || '',
      equipmentTags: form.equipmentTags || '',
      status: 'draft',
      createdAt: parsed.savedAt || new Date().toISOString(),
      isLocalDraft: true,
    };
  } catch {
    return null;
  }
}

export function RFQsPage({
  embedded = false,
  pipeline = 'sales',
}: {
  embedded?: boolean;
  /** sales = customer RFQs received; purchases = admin RFQs sent to vendors */
  pipeline?: 'sales' | 'purchases';
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { role } = usePermissions();
  const { user, permissions } = useAuth();
  const { showToast } = useToast();
  const isSuperAdmin = role === 'super_admin';
  const canExport = canExportRfqData(user?.role, 'admin', user?.roleName, permissions);
  const isPurchases = pipeline === 'purchases';
  const senderType = isPurchases ? 'admin' : 'customer';
  const detailBase = isPurchases
    ? '/dashboard/admin/purchases?purchasesTab=rfqs'
    : '/dashboard/admin/sales?salesTab=rfqs';

  const [activeFilter, setActiveFilter] = useState<PipelineTab>(() =>
    parsePipelineTab(searchParams.get('tab'))
  );
  const [searchFilters, setSearchFilters] = useState({
    rfqNo: '',
    category: '',
    brand: '',
    model: '',
    vesselName: '',
    contactNo: '',
    department: '',
    vesselEntity: '',
    logisticDetails: '',
    currentState: '',
    receivedDate: '',
    action: '',
  });
  const [page, setPage] = useState(1);
  const [localDraft, setLocalDraft] = useState<RFQ | null>(() => readLocalDraftRfq());
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const PAGE_SIZE = 10;

  useEffect(() => {
    const tab = parsePipelineTab(searchParams.get('tab'));
    if (embedded && (tab === 'unconfirmed_po' || tab === 'confirmed_po')) {
      setActiveFilter('all');
      return;
    }
    setActiveFilter(tab);
  }, [searchParams, embedded]);

  useEffect(() => {
    const q = searchParams.get('search');
    if (q) setSearchFilters((prev) => ({ ...prev, rfqNo: q }));
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [activeFilter, searchFilters]);

  useEffect(() => {
    const refreshLocal = () => setLocalDraft(readLocalDraftRfq());
    refreshLocal();
    window.addEventListener('focus', refreshLocal);
    window.addEventListener('storage', refreshLocal);
    return () => {
      window.removeEventListener('focus', refreshLocal);
      window.removeEventListener('storage', refreshLocal);
    };
  }, []);

  const {
    data: rfqs = [],
    isLoading,
  } = useQuery<RFQ[]>({
    queryKey: ['admin-rfqs-pipeline', senderType],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `/api/v1/admin/rfqs-all?senderType=${senderType}&limit=200`
      );
      if (!response.ok) throw new Error('Failed to fetch RFQs');
      const responseBody = await response.json();
      if (Array.isArray(responseBody.data)) return responseBody.data;
      if (responseBody.data && Array.isArray(responseBody.data.data)) return responseBody.data.data;
      return [];
    },
  });

  const isRejected = (rfq: RFQ) => {
    const normalized = (rfq.status || '').toLowerCase();
    return normalized === 'cancelled' || normalized === 'rejected';
  };

  const isDraftStatus = (rfq: RFQ) =>
    (rfq.status || '').toLowerCase() === 'draft' || !!rfq.isLocalDraft;

  const setFilter = (id: PipelineTab) => {
    setActiveFilter(id);
    const next = new URLSearchParams(searchParams);
    if (id === 'all') {
      next.set('tab', 'all');
    } else {
      next.set('tab', id);
    }
    setSearchParams(next, { replace: true });
  };

  const filterLabels: Record<PipelineTab, string> = {
    all: 'All',
    drafts: 'Drafts',
    unquoted: 'Unquoted RFQs',
    quoted: 'Quoted RFQs',
    unconfirmed_po: 'Unconfirmed POs',
    confirmed_po: 'Confirmed POs',
    rejected: 'Rejected RFQs',
  };

  const pipelineRfqs = useMemo(() => {
    // Browser draft is only for admin-raised purchase RFQs
    if (!isPurchases || !localDraft) return rfqs;
    if (rfqs.some((r) => r.isLocalDraft)) return rfqs;
    return [localDraft, ...rfqs];
  }, [rfqs, localDraft, isPurchases]);

  const isPoStageStatus = (status: string) =>
    status === 'ordered' ||
    status === 'partially_ordered' ||
    status === 'order-confirmed' ||
    status === 'finalized' ||
    status === 'awarded' ||
    status === 'acknowledged' ||
    status === 'issued' ||
    status === 'completed' ||
    status === 'shipped' ||
    status === 'delivered' ||
    status === 'fulfilled';

  const counts = useMemo(() => {
    const countsObj = {
      all: 0,
      drafts: 0,
      unquoted: 0,
      quoted: 0,
      unconfirmed_po: 0,
      confirmed_po: 0,
      rejected: 0,
    };

    pipelineRfqs.forEach((rfq) => {
      const status = (rfq.status || '').toLowerCase();
      if (isDraftStatus(rfq)) {
        countsObj.drafts++;
        if (!embedded) countsObj.all++;
        else if (!isPoStageStatus(status)) countsObj.all++;
        return;
      }
      if (isRejected(rfq)) {
        countsObj.rejected++;
        countsObj.all++;
        return;
      }
      if (status === 'sent' || status === 'pending') {
        countsObj.unquoted++;
        countsObj.all++;
      } else if (status === 'quoted') {
        countsObj.quoted++;
        countsObj.all++;
      } else if (status === 'ordered' || status === 'acknowledged' || status === 'issued') {
        countsObj.unconfirmed_po++;
        if (!embedded) countsObj.all++;
      } else if (
        status === 'completed' ||
        status === 'shipped' ||
        status === 'delivered' ||
        status === 'fulfilled'
      ) {
        countsObj.confirmed_po++;
        if (!embedded) countsObj.all++;
      } else {
        countsObj.all++;
      }
    });

    return countsObj;
  }, [pipelineRfqs, embedded]);

  const filteredRFQs = useMemo(() => {
    return pipelineRfqs
      .filter((rfq) => {
        const status = (rfq.status || '').toLowerCase();
        const rejected = isRejected(rfq);
        const draft = isDraftStatus(rfq);

        // Sales RFQ tab: ordered RFQs live under Purchase Orders / In Progress
        if (embedded && isPoStageStatus(status) && !rejected && !draft) {
          if (activeFilter !== 'rejected') return false;
        }

        switch (activeFilter) {
          case 'all':
            return true;
          case 'drafts':
            return draft;
          case 'unquoted':
            return (status === 'sent' || status === 'pending') && !rejected && !draft;
          case 'quoted':
            return status === 'quoted' && !rejected;
          case 'unconfirmed_po':
            return (
              (status === 'ordered' ||
                status === 'partially_ordered' ||
                status === 'order-confirmed' ||
                status === 'acknowledged' ||
                status === 'issued') &&
              !rejected
            );
          case 'confirmed_po':
            return (
              (status === 'completed' ||
                status === 'shipped' ||
                status === 'delivered' ||
                status === 'fulfilled') &&
              !rejected
            );
          case 'rejected':
            return rejected;
          default:
            return true;
        }
      })
      .filter((rfq) => {
        const categoryQ = searchFilters.category.trim().toLowerCase();
        const brandQ = searchFilters.brand.trim().toLowerCase();
        const modelQ = searchFilters.model.trim().toLowerCase();
        const rfqQ = searchFilters.rfqNo.trim().toLowerCase();
        const vesselQ = searchFilters.vesselName.trim().toLowerCase();
        const contactQ = searchFilters.contactNo.trim().toLowerCase();
        const deptQ = searchFilters.department.trim().toLowerCase();
        const vesselEntityQ = searchFilters.vesselEntity.trim().toLowerCase();
        const logisticQ = searchFilters.logisticDetails.trim().toLowerCase();
        const currentStateQ = searchFilters.currentState.trim().toLowerCase();
        const receivedDateQ = searchFilters.receivedDate.trim().toLowerCase();
        const actionQ = searchFilters.action.trim().toLowerCase();
        if (
          !categoryQ &&
          !brandQ &&
          !modelQ &&
          !rfqQ &&
          !vesselQ &&
          !contactQ &&
          !deptQ &&
          !vesselEntityQ &&
          !logisticQ &&
          !currentStateQ &&
          !receivedDateQ &&
          !actionQ
        ) {
          return true;
        }

        const category = `${rfq.category || ''} ${rfq.subCategory || ''}`.toLowerCase();
        const model = String(rfq.equipmentModel || rfq.metadata?.model || '').toLowerCase();
        const contact = String(
          rfq.metadata?.contactPhone || rfq.metadata?.phone || rfq.metadata?.contactNo || ''
        ).toLowerCase();
        const department = String(rfq.department || rfq.metadata?.department || '').toLowerCase();
        const vessel = `${rfq.vesselId?.name || ''} ${rfq.vesselName || ''}`.toLowerCase();
        const vesselEntity = `${rfq.vesselId?.name || ''} ${rfq.vesselName || ''} ${rfq.organizationId?.name || ''} ${rfq.rfqNumber || ''} ${rfq.equipmentTags || ''}`.toLowerCase();
        const logisticDetails = `${rfq.supplyPort || ''} ${rfq.brand || ''}`.toLowerCase();
        const statusNorm = (rfq.status || '').toLowerCase();
        const currentState = (
          isDraftStatus(rfq)
            ? rfq.isLocalDraft
              ? 'Browser Draft'
              : 'Draft'
            : isRejected(rfq)
              ? 'Rejected'
              : statusNorm === 'sent' || statusNorm === 'pending'
                ? 'Pending'
                : statusNorm === 'quoted'
                  ? 'Quoted'
                  : statusNorm === 'ordered' || statusNorm === 'acknowledged' || statusNorm === 'issued'
                    ? 'PO Received'
                    : statusNorm === 'completed' || statusNorm === 'fulfilled'
                      ? 'Fulfilled'
                      : statusNorm === 'shipped' || statusNorm === 'delivered'
                        ? 'Confirmed'
                        : rfq.status || ''
        ).toLowerCase();
        const receivedDate = `${formatServerDate(rfq.createdAt)} ${formatServerTime(rfq.createdAt)}`.toLowerCase();
        const action = 'view edit delete';

        if (categoryQ && !category.includes(categoryQ)) return false;
        if (brandQ && !(rfq.brand || '').toLowerCase().includes(brandQ)) return false;
        if (modelQ && !model.includes(modelQ)) return false;
        if (rfqQ && !(rfq.rfqNumber || '').toLowerCase().includes(rfqQ) && !(rfq.title || '').toLowerCase().includes(rfqQ)) return false;
        if (vesselQ && !vessel.includes(vesselQ)) return false;
        if (contactQ && !contact.includes(contactQ)) return false;
        if (deptQ && !department.includes(deptQ)) return false;
        if (vesselEntityQ && !vesselEntity.includes(vesselEntityQ)) return false;
        if (logisticQ && !logisticDetails.includes(logisticQ)) return false;
        if (currentStateQ && !currentState.includes(currentStateQ)) return false;
        if (receivedDateQ && !receivedDate.includes(receivedDateQ)) return false;
        if (actionQ && !action.includes(actionQ)) return false;
        return true;
      });
  }, [pipelineRfqs, activeFilter, searchFilters, embedded]);

  const totalPages = Math.max(1, Math.ceil(filteredRFQs.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedRFQs = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRFQs.slice(start, start + PAGE_SIZE);
  }, [filteredRFQs, currentPage]);

  const handleExportData = (format: RfqExportFormat) => {
    const rows: RfqExportRow[] = filteredRFQs.map((rfq) => ({
      rfqNumber: rfq.rfqNumber,
      title: rfq.title,
      vesselName: rfq.vesselId?.name || rfq.vesselName || '',
      imoNumber: rfq.vesselId?.imoNumber,
      supplyPort: rfq.supplyPort,
      brand: rfq.brand,
      category: rfq.category,
      status: statusLabel(rfq),
      createdAt: rfq.createdAt
        ? `${formatServerDate(rfq.createdAt)} ${formatServerTime(rfq.createdAt)}`
        : '',
      dueDate: rfq.dueDate ? formatServerDate(rfq.dueDate) : '',
      senderName: rfq.organizationId?.name,
    }));

    const tabLabel = activeFilter.replace(/_/g, ' ');

    try {
      if (format === 'excel') {
        downloadRfqDataExcel(rows, {
          filenamePrefix: isPurchases ? 'Admin_Purchase_RFQ_Export' : 'Admin_Sales_RFQ_Export',
          sheetName: 'RFQs',
        });
      } else {
        downloadRfqDataPdf(rows, {
          filenamePrefix: isPurchases ? 'Admin_Purchase_RFQ_Export' : 'Admin_Sales_RFQ_Export',
          title: isPurchases ? 'Admin Purchase RFQ Export' : 'Admin Sales RFQ Export',
          subtitle: `Tab: ${tabLabel} · Generated ${new Date().toLocaleString()}`,
        });
      }
      showToast(`Exported ${rows.length} RFQ${rows.length === 1 ? '' : 's'} as ${format.toUpperCase()}`, 'success');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to export RFQ data';
      showToast(message, 'error');
      throw err;
    }
  };

  const openRfq = (rfq: RFQ) => {
    if (rfq.isLocalDraft) {
      navigate('/dashboard/admin/rfqs/new');
      return;
    }
    if (isDraftStatus(rfq)) {
      navigate(`/dashboard/admin/rfqs/new?draftId=${rfq._id}`);
      return;
    }
    navigate(`${detailBase}&rfqId=${rfq._id}`);
  };

  const statusBadge = (rfq: RFQ) => {
    const status = (rfq.status || '').toLowerCase();
    const rejected = isRejected(rfq);
    return cn(
      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold uppercase tracking-wide border',
      rejected
        ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/40'
        : isDraftStatus(rfq)
          ? 'bg-slate-50 dark:bg-slate-950/40 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800/40'
          : status === 'sent' || status === 'pending'
            ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40'
            : status === 'quoted'
              ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/40'
              : status === 'ordered' || status === 'acknowledged' || status === 'issued'
                ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/40'
                : status === 'cancelled' || status === 'rejected'
                  ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/40'
                  : 'bg-gray-50 dark:bg-gray-800/40 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800/30'
    );
  };

  const statusLabel = (rfq: RFQ) => {
    const status = (rfq.status || '').toLowerCase();
    if (isDraftStatus(rfq)) return rfq.isLocalDraft ? 'Browser Draft' : 'Draft';
    if (isRejected(rfq)) return 'Rejected';
    if (status === 'sent' || status === 'pending') return 'Pending';
    if (status === 'quoted') return 'Quoted';
    if (status === 'ordered' || status === 'acknowledged' || status === 'issued') return 'PO Received';
    if (status === 'completed' || status === 'fulfilled') return 'Fulfilled';
    if (status === 'shipped' || status === 'delivered') return 'Confirmed';
    return rfq.status;
  };

  return (
    <div
      className={cn(
        'w-full font-sans text-[hsl(var(--foreground))]',
        embedded ? '' : 'min-h-screen bg-[hsl(var(--background))] p-4 lg:p-8'
      )}
    >
      {!embedded && (
        <PageHeader
          className="[&_.erp-page-title]:!text-xl [&_.erp-page-title]:!font-bold [&_.erp-page-title]:!leading-snug [&_.erp-page-subtitle]:!text-xs [&_.erp-page-subtitle]:!mt-1"
          title={
            <>
              {isPurchases ? 'Purchases' : 'Sales'}
              <MdInfo
                className="inline-block ml-1.5 align-middle text-[hsl(var(--primary))/70] w-4 h-4 cursor-pointer hover:text-[hsl(var(--primary))] transition-colors"
                title={
                  isPurchases
                    ? 'Raise RFQs to vendors and track their quotations'
                    : 'Manage customer RFQs received in Sales'
                }
              />
            </>
          }
          subtitle={
            isPurchases
              ? 'Create RFQs for vendors, review quotations, and send purchase orders.'
              : 'Track customer RFQs received, offers you send, and related sales orders.'
          }
          subtitleVariant="description"
          subtitleAccent={false}
          breadcrumb={[
            { label: 'Admin' },
            { label: isPurchases ? 'Purchases' : 'Sales', current: true },
          ]}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {canExport && (
                <button
                  type="button"
                  onClick={() => setIsExportModalOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#0088cc]/30 bg-[#0088cc]/5 px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-[#0088cc] shadow-sm transition-colors hover:bg-[#0088cc]/10"
                >
                  <MdDownload className="h-5 w-5" />
                  Export Data
                </button>
              )}
              {isPurchases ? (
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/admin/rfqs/new')}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0088cc] px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-white shadow-sm transition-colors hover:bg-[#0077b3]"
                >
                  <MdAdd className="h-5 w-5" />
                  Create RFQ
                </button>
              ) : null}
            </div>
          }
        />
      )}

      <div className="mb-8 min-w-0 overflow-x-auto">
        {embedded && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              {isPurchases ? (
                <>
                  Raise RFQs to vendors. After you finalize a vendor offer, the order moves to{' '}
                  <span className="font-semibold text-[hsl(var(--foreground))]">Purchase Orders</span>.
                </>
              ) : (
                <>
                  Active customer RFQs only. After a customer accepts your offer, the order moves to{' '}
                  <span className="font-semibold text-[hsl(var(--foreground))]">Purchase Orders</span>.
                </>
              )}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {canExport && (
                <button
                  type="button"
                  onClick={() => setIsExportModalOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#0088cc]/30 bg-[#0088cc]/5 px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-[#0088cc] shadow-sm transition-colors hover:bg-[#0088cc]/10"
                >
                  <MdDownload className="h-5 w-5" />
                  Export Data
                </button>
              )}
              {isPurchases && (
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/admin/rfqs/new')}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0088cc] px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-white shadow-sm transition-colors hover:bg-[#0077b3]"
                >
                  <MdAdd className="h-5 w-5" />
                  Create RFQ
                </button>
              )}
            </div>
          </div>
        )}
        <div className={cn('grid gap-2 min-w-0 lg:min-w-full', embedded ? 'grid-cols-5' : 'grid-cols-7')}>
        {(
          [
            { id: 'all' as const, label: 'All', icon: MdDescription, color: 'primary' },
            { id: 'drafts' as const, label: 'Drafts', icon: MdSave, color: 'slate' },
            { id: 'unquoted' as const, label: 'Unquoted RFQs', icon: MdFolderOpen, color: 'blue' },
            { id: 'quoted' as const, label: 'Quoted RFQs', icon: MdDescription, color: 'amber' },
            ...(!embedded
              ? ([
                  {
                    id: 'unconfirmed_po' as const,
                    label: 'Unconfirmed POs',
                    icon: MdFolderOpen,
                    color: 'purple',
                  },
                  {
                    id: 'confirmed_po' as const,
                    label: 'Confirmed POs',
                    icon: MdDescription,
                    color: 'emerald',
                  },
                ] as const)
              : []),
            { id: 'rejected' as const, label: 'Rejected RFQs', icon: MdCancel, color: 'rose' },
          ] as const
        ).map((card) => {
          const Icon = card.icon;
          const active = activeFilter === card.id;
          const colorMap: Record<string, string> = {
            primary: 'bg-[#0088cc]/10 text-[#0088cc]',
            slate: 'bg-slate-500/10 text-slate-500',
            blue: 'bg-blue-500/10 text-blue-500',
            amber: 'bg-amber-500/10 text-amber-500',
            purple: 'bg-purple-500/10 text-purple-500',
            emerald: 'bg-emerald-500/10 text-emerald-500',
            rose: 'bg-rose-500/10 text-rose-500',
          };
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => setFilter(card.id)}
              className={cn(
                'bg-[hsl(var(--card))] px-2.5 py-2 rounded-lg border shadow-sm flex items-stretch relative group text-left transition-colors min-w-0',
                active
                  ? card.color === 'rose'
                    ? 'border-rose-400 ring-1 ring-rose-300/50'
                    : 'border-[#0088cc] ring-1 ring-[#0088cc]/30'
                  : 'border-[hsl(var(--border))] hover:border-[#0088cc]/40'
              )}
            >
              <div className="flex items-center gap-2 min-w-0 w-full">
                <div
                  className={cn(
                    'w-7 h-7 shrink-0 rounded-md flex items-center justify-center',
                    colorMap[card.color]
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[8px] font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-wide leading-tight whitespace-normal break-words">
                    {card.label}
                  </p>
                  <p className="text-base font-bold text-[hsl(var(--foreground))] mt-0.5 leading-none">
                    {counts[card.id]}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
        </div>
      </div>

      <div className="erp-table-shell bg-[hsl(var(--card))] border border-[hsl(var(--border))] min-h-[500px] flex flex-col shadow-sm rounded-2xl overflow-hidden">
        <div className="erp-table-toolbar border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))/10] flex flex-col gap-3 p-4">
          <span className="text-xs text-[hsl(var(--muted-foreground))] font-semibold">
            Showing {filteredRFQs.length} of {pipelineRfqs.length} entries
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-2">
            {(
              [
                ['rfqNo', 'RFQ No'],
                ['category', 'Category'],
                ['brand', 'RFQ Brand'],
                ['model', 'Model'],
                ['vesselName', 'Vessel Name'],
                ['contactNo', 'Contact No'],
                ['department', 'Department'],
                ['vesselEntity', 'Vessel & Entity'],
                ['logisticDetails', 'Logistic Details'],
                ['currentState', 'Current State'],
                ['receivedDate', 'Received Date'],
                ['action', 'Action'],
              ] as const
            ).map(([key, placeholder]) => (
              <div key={key} className="relative">
                <MdSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] w-4 h-4" />
                <input
                  type="text"
                  placeholder={placeholder}
                  value={searchFilters[key]}
                  onChange={(e) => setSearchFilters((prev) => ({ ...prev, [key]: e.target.value }))}
                  className="w-full pl-8 pr-3 py-1.5 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:border-[#0088cc] focus:ring-1 focus:ring-[#0088cc]/30"
                />
              </div>
            ))}
          </div>
          <ClearFiltersButton
            visible={Object.values(searchFilters).some((value) => value.trim() !== '')}
            onClick={() =>
              setSearchFilters({
                rfqNo: '',
                category: '',
                brand: '',
                model: '',
                vesselName: '',
                contactNo: '',
                department: '',
                vesselEntity: '',
                logisticDetails: '',
                currentState: '',
                receivedDate: '',
                action: '',
              })
            }
            variant="toolbar"
          />
        </div>

        <div className="erp-table-scroll flex-1 relative overflow-y-auto max-h-[600px]">
          <table className="w-full text-left text-sm whitespace-nowrap border-collapse">
            <thead>
              <tr className="erp-table-head-row bg-[hsl(var(--muted))/30]">
                <th className="erp-table-head-cell text-[10px] font-bold text-[hsl(var(--foreground))] uppercase tracking-widest px-6 py-4 sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                  RFQ No
                </th>
                <th className="erp-table-head-cell text-[10px] font-bold text-[hsl(var(--foreground))] uppercase tracking-widest px-6 py-4 sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                  Category
                </th>
                <th className="erp-table-head-cell text-[10px] font-bold text-[hsl(var(--foreground))] uppercase tracking-widest px-6 py-4 sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                  RFQ Brand
                </th>
                <th className="erp-table-head-cell text-[10px] font-bold text-[hsl(var(--foreground))] uppercase tracking-widest px-6 py-4 sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                  Model
                </th>
                <th className="erp-table-head-cell text-[10px] font-bold text-[hsl(var(--foreground))] uppercase tracking-widest px-6 py-4 sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                  Vessel Name
                </th>
                <th className="erp-table-head-cell text-[10px] font-bold text-[hsl(var(--foreground))] uppercase tracking-widest px-6 py-4 sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                  Contact No
                </th>
                <th className="erp-table-head-cell text-[10px] font-bold text-[hsl(var(--foreground))] uppercase tracking-widest px-6 py-4 sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                  Department
                </th>
                <th className="erp-table-head-cell text-[10px] font-bold text-[hsl(var(--foreground))] uppercase tracking-widest px-6 py-4 sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                  Vessel & Entity
                </th>
                <th className="erp-table-head-cell text-[10px] font-bold text-[hsl(var(--foreground))] uppercase tracking-widest px-6 py-4 sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                  Logistic Details
                </th>
                <th className="erp-table-head-cell text-[10px] font-bold text-[hsl(var(--foreground))] uppercase tracking-widest px-6 py-4 sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                  Current State
                </th>
                <th className="erp-table-head-cell text-[10px] font-bold text-[hsl(var(--foreground))] uppercase tracking-widest px-6 py-4 sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                  <div className="flex items-center gap-1">
                    {activeFilter === 'drafts' ? 'Saved Date' : 'Received Date'}{' '}
                    <MdKeyboardArrowDown className="text-[hsl(var(--muted-foreground))] w-4 h-4" />
                  </div>
                </th>
                <th className="erp-table-head-cell text-[10px] font-bold text-[hsl(var(--foreground))] uppercase tracking-widest px-6 py-4 text-center sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-[hsl(var(--card))]">
              {isLoading ? (
                <tr>
                  <td colSpan={12} className="p-6">
                    <PageSkeleton variant="panel" />
                  </td>
                </tr>
              ) : filteredRFQs.length === 0 ? (
                <tr>
                  <td colSpan={12} className="p-0">
                    <div className="erp-table-empty m-6 p-12 text-center flex flex-col items-center justify-center border border-[hsl(var(--border))] bg-[hsl(var(--muted))/5] rounded-xl">
                      <div className="w-12 h-12 bg-[#0088cc]/10 text-[#0088cc] rounded-full flex items-center justify-center mb-3">
                        {activeFilter === 'drafts' ? (
                          <MdSave className="w-6 h-6" />
                        ) : (
                          <MdFolderOpen className="w-6 h-6" />
                        )}
                      </div>
                      <h3 className="text-base font-bold text-[hsl(var(--foreground))] mb-1">
                        {activeFilter === 'drafts' ? 'No saved drafts' : 'No RFQs found'}
                      </h3>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-xs">
                        {activeFilter === 'drafts'
                          ? 'Draft RFQs will appear here when saved with status Draft.'
                          : `There are no ${filterLabels[activeFilter] || 'Records'} matching your criteria.`}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedRFQs.map((rfq) => {
                  const contactNo = String(
                    rfq.metadata?.contactPhone || rfq.metadata?.phone || rfq.metadata?.contactNo || ''
                  );
                  const department = String(rfq.department || rfq.metadata?.department || '');
                  const model = String(rfq.equipmentModel || rfq.metadata?.model || '');
                  const categoryLabel = [rfq.category, rfq.subCategory].filter(Boolean).join(' / ');

                  return (
                    <tr
                      key={rfq._id}
                      onClick={() => openRfq(rfq)}
                      className="erp-table-row erp-table-row--interactive border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))/20] cursor-pointer"
                    >
                      <td className="px-6 py-5">
                        <div className="text-[hsl(var(--foreground))] font-semibold text-sm font-mono">
                          {rfq.rfqNumber || rfq._id.slice(-8)}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-[hsl(var(--foreground))] font-medium text-sm">
                          {categoryLabel || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-[hsl(var(--foreground))] font-medium text-sm">
                          {rfq.brand || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-[hsl(var(--foreground))] font-medium text-sm">
                          {model || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-[hsl(var(--foreground))] font-medium text-sm">
                          {rfq.vesselId?.name || rfq.vesselName || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-[hsl(var(--foreground))] font-medium text-sm">
                          {contactNo || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-[hsl(var(--foreground))] font-medium text-sm capitalize">
                          {department || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-[#0088cc] font-semibold text-sm hover:underline">
                          {rfq.vesselId?.name || rfq.vesselName || 'Global Vessel'}
                        </div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 font-mono">
                          Ref: {rfq.rfqNumber || rfq._id.slice(-8)}
                        </div>
                        {rfq.organizationId?.name && (
                          <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1 uppercase tracking-wider">
                            {rfq.organizationId.name}
                          </div>
                        )}
                        {rfq.equipmentTags && (
                          <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                            Tags: {rfq.equipmentTags}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-1.5 text-[hsl(var(--foreground))] font-medium mb-1.5 text-sm">
                          <MdLocationOn className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                          {rfq.supplyPort || (rfq.isLocalDraft ? 'Not set' : 'Standard Port')}
                        </div>
                        {rfq.brand && (
                          <div className="flex items-center gap-1.5 text-[10px] text-[#0088cc] font-bold uppercase tracking-widest px-2 py-0.5 bg-blue-500/10 rounded border border-blue-500/20 w-fit">
                            <MdBrandingWatermark className="w-3.5 h-3.5" />
                            {rfq.brand}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-5">
                        <span className={statusBadge(rfq)}>{statusLabel(rfq)}</span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-[hsl(var(--foreground))] font-semibold text-sm">
                          {formatServerDate(rfq.createdAt)}
                        </div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 font-medium">
                          {formatServerTime(rfq.createdAt)} UTC
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center relative">
                          <button
                            type="button"
                            className="p-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] rounded transition-colors"
                            onClick={() => setOpenMenuId((id) => (id === rfq._id ? null : rfq._id))}
                          >
                            <MdMoreVert className="w-4 h-4" />
                          </button>
                          {openMenuId === rfq._id && (
                            <div className="absolute right-0 top-full mt-1 z-30 min-w-[140px] rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-lg py-1 text-left">
                              <button
                                type="button"
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[hsl(var(--muted))]"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  openRfq(rfq);
                                }}
                              >
                                <MdVisibility className="w-4 h-4" /> View
                              </button>
                              <button
                                type="button"
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[hsl(var(--muted))]"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  if (rfq.isLocalDraft) {
                                    navigate('/dashboard/admin/rfqs/new');
                                    return;
                                  }
                                  navigate(`/dashboard/admin/rfqs/new?draftId=${rfq._id}`);
                                }}
                              >
                                <MdEdit className="w-4 h-4" /> Edit
                              </button>
                              {isSuperAdmin && (
                                <button
                                  type="button"
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                  onClick={async () => {
                                    setOpenMenuId(null);
                                    if (!window.confirm(`Delete RFQ ${rfq.rfqNumber}? This cannot be undone.`)) {
                                      return;
                                    }
                                    try {
                                      if (rfq.isLocalDraft) {
                                        localStorage.removeItem(LOCAL_DRAFT_KEY);
                                        setLocalDraft(null);
                                        showToast('Draft deleted', 'success');
                                        return;
                                      }
                                      const res = await authenticatedFetch(`/api/v1/admin/rfq/${rfq._id}`, {
                                        method: 'DELETE',
                                      });
                                      const json = await res.json().catch(() => ({}));
                                      if (!res.ok) throw new Error(json.error || 'Failed to delete RFQ');
                                      showToast('RFQ deleted', 'success');
                                      await queryClient.invalidateQueries({ queryKey: ['admin-rfqs-pipeline'] });
                                      await queryClient.invalidateQueries({ queryKey: ['admin-pipeline-overview'] });
                                      await queryClient.invalidateQueries({ queryKey: ['admin-dashboard-stats'] });
                                    } catch (err: any) {
                                      showToast(err.message || 'Failed to delete RFQ', 'error');
                                    }
                                  }}
                                >
                                  <MdDelete className="w-4 h-4" /> Delete
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 flex items-center justify-between border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]">
          <span className="text-xs text-[hsl(var(--muted-foreground))] font-medium">
            {filteredRFQs.length === 0
              ? 'Showing 0 entries'
              : `Showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, filteredRFQs.length)} of ${filteredRFQs.length} entries`}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className={cn(
                'px-4 py-2 text-xs border border-[hsl(var(--border))] rounded bg-[hsl(var(--card))] transition-colors',
                currentPage <= 1
                  ? 'text-[hsl(var(--muted-foreground))] cursor-not-allowed opacity-50'
                  : 'text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]'
              )}
            >
              Previous
            </button>
            <span className="text-xs font-medium text-[hsl(var(--muted-foreground))] px-1">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className={cn(
                'px-4 py-2 text-xs border border-[hsl(var(--border))] rounded bg-[hsl(var(--card))] transition-colors',
                currentPage >= totalPages
                  ? 'text-[hsl(var(--muted-foreground))] cursor-not-allowed opacity-50'
                  : 'text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]'
              )}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {canExport && (
        <ExportDataModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          onExport={handleExportData}
          recordCount={filteredRFQs.length}
          title="Export RFQ Data"
        />
      )}
    </div>
  );
}
